import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import API_BASE_URL from "../config";
import {
  Users, MessageCircle, CheckCircle, XCircle, Shield,
  Trash2, Edit2, Hash, Award, X, Save, TrendingUp, Heart
} from "lucide-react";
import { toast } from "sonner";

const API = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

const VALID_BADGES = ["new_traveler", "creator", "active_reviewer", "local_expert", "admin"];
const BADGE_COLORS = {
  admin: "bg-red-100 text-red-700 border-red-200",
  local_expert: "bg-[#F59E0B]/10 text-[#D97706] border-[#F59E0B]/20",
  active_reviewer: "bg-[#3FAF7A]/10 text-[#3FAF7A] border-[#3FAF7A]/20",
  creator: "bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20",
  new_traveler: "bg-[#E2E8E4] text-[#5C6B64] border-[#E2E8E4]",
  premium: "bg-yellow-100 text-yellow-700 border-yellow-200",
};

const PLAN_COLORS = {
  free: "bg-[#E2E8E4] text-[#5C6B64]",
  premium: "bg-yellow-100 text-yellow-700",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [pendingPlaces, setPendingPlaces] = useState([]);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [editChannel, setEditChannel] = useState(null);
  const [badgeUser, setBadgeUser] = useState(null);



  const authConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    withCredentials: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, placesRes, usersRes, postsRes, channelsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`, authConfig()),
        axios.get(`${API}/admin/places/pending`, authConfig()),
        axios.get(`${API}/admin/users`, authConfig()),
        axios.get(`${API}/admin/posts`, authConfig()),
        axios.get(`${API}/admin/channels`, authConfig()),
      ]);
      setStats(statsRes.data);
      setPendingPlaces(placesRes.data.places || []);
      setUsers(usersRes.data.users || []);
      setPosts(postsRes.data.posts || []);
      setChannels(channelsRes.data.channels || []);
    } catch {
      toast.error("Failed to load admin data");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleVerify = async (placeId) => {
    try {
      await axios.post(`${API}/places/${placeId}/verify`, {}, authConfig());
      setPendingPlaces((p) => p.filter((x) => x.id !== placeId));
      toast.success("Place verified!");
      load();
    } catch { toast.error("Failed to verify"); }
  };

  const handleReject = async (placeId) => {
    try {
      await axios.post(`${API}/places/${placeId}/reject`, {}, authConfig());
      setPendingPlaces((p) => p.filter((x) => x.id !== placeId));
      toast.success("Place rejected");
      load();
    } catch { toast.error("Failed to reject"); }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role }, authConfig());
      setUsers((p) => p.map((u) => u.id === userId ? { ...u, role } : u));
      toast.success("Role updated");
    } catch { toast.error("Failed to update role"); }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, authConfig());
      setUsers((p) => p.filter((u) => u.id !== userId));
      toast.success("User deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleGrantPremium = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/subscription`, { plan: "premium", granted_by_admin: true }, authConfig());
      setUsers((p) => p.map((u) => u.id === userId ? { ...u, subscription_plan: "premium" } : u));
      toast.success("Premium granted!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to grant premium");
    }
  };

  const handleRevokePremium = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/subscription`, { plan: "free", granted_by_admin: false }, authConfig());
      setUsers((p) => p.map((u) => u.id === userId ? { ...u, subscription_plan: "free" } : u));
      toast.success("Premium revoked");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to revoke premium");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/admin/posts/${postId}`, authConfig());
      setPosts((p) => p.filter((x) => x.id !== postId));
      toast.success("Post deleted");
      load();
    } catch { toast.error("Failed to delete post"); }
  };

  const handleUpdateBadges = async (userId, badges) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/badges`, { badges }, authConfig());
      setUsers((p) => p.map((u) => u.id === userId ? { ...u, badges } : u));
      setBadgeUser(null);
      toast.success("Badges updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update badges"); }
  };

  const handleUpdateChannel = async (channelId, data) => {
    try {
      await axios.put(`${API}/channels/${channelId}`, data, authConfig());
      setEditChannel(null);
      toast.success("Channel updated");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };

  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm("Delete this channel? It must have no posts.")) return;
    try {
      await axios.delete(`${API}/channels/${channelId}`, authConfig());
      toast.success("Channel deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-pulse" data-testid="admin-loading">
        <div className="h-8 bg-muted rounded w-48 mb-8" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 bg-muted rounded-2xl" />)}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
      <div className="flex items-center gap-3 mb-8">
        <Shield className="w-6 h-6 text-primary" strokeWidth={1.5} />
        <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-foreground">Admin Dashboard</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          {[
            { label: "Users", value: stats.users, icon: Users, color: "#123D2B" },
            { label: "Posts", value: stats.posts, icon: MessageCircle, color: "#3B82F6" },
            { label: "Engagement", value: `${stats.engagement_rate}%`, icon: TrendingUp, color: "#3FAF7A" },
            { label: "Total Likes", value: stats.likes, icon: Heart, color: "#F59E0B" },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-card border border-border rounded-2xl p-5" data-testid={`stat-${s.label.toLowerCase()}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: s.color + "10" }}>
                    <Icon className="w-5 h-5" style={{ color: s.color }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{s.value}</p>
                    <p className="text-xs text-muted-foreground">{s.label}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-card border border-border rounded-full p-0.5 w-fit overflow-x-auto">
        {["overview", "posts", "users", "channels", "analytics"].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            data-testid={`tab-${t}`}>
            {t === "overview" ? "Pending Places" : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Pending Places */}
      {tab === "overview" && (
        <div className="space-y-4" data-testid="pending-places-list">
          {pendingPlaces.length === 0 ? (
            <div className="bg-card border border-border rounded-2xl p-8 text-center">
              <CheckCircle className="w-10 h-10 text-primary mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-muted-foreground">No pending places to review</p>
            </div>
          ) : pendingPlaces.map((place) => (
            <div key={place.id} className="bg-card border border-border rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`pending-${place.id}`}>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-foreground truncate">{place.name}</h3>
                <p className="text-sm text-muted-foreground line-clamp-1">{place.description}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  <span className="bg-background border border-border px-2 py-0.5 rounded-full">{place.category?.replace("_", " ")}</span>
                  <span>by {place.created_by_name || "Unknown"}</span>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => handleVerify(place.id)} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1.5" data-testid={`verify-${place.id}`}>
                  <CheckCircle className="w-4 h-4" strokeWidth={1.5} /> Verify
                </button>
                <button onClick={() => handleReject(place.id)} className="bg-card border border-red-200 text-red-600 hover:bg-red-500/10 rounded-full px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1.5" data-testid={`reject-${place.id}`}>
                  <XCircle className="w-4 h-4" strokeWidth={1.5} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* All Posts */}
      {tab === "posts" && (
        <div className="space-y-3" data-testid="admin-posts-list">
          {posts.length === 0 ? (
            <div className="bg-card border border-border rounded-2xl p-8 text-center">
              <p className="text-muted-foreground">No posts yet</p>
            </div>
          ) : posts.map((p) => (
            <div key={p.id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-4" data-testid={`admin-post-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs font-bold uppercase text-muted-foreground">{p.type}</span>
                  <span className="text-xs text-muted-foreground">in {p.channel?.name || "?"}</span>
                </div>
                <h3 className="font-medium text-sm text-foreground truncate">{p.title}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">by {p.author?.name || "Unknown"} - {new Date(p.created_at).toLocaleDateString()}</p>
              </div>
              <button onClick={() => handleDeletePost(p.id)} className="text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-colors shrink-0" data-testid={`delete-post-${p.id}`}>
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Users */}
      {tab === "users" && (
        <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="users-list">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background">
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-5 py-3">User</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-5 py-3 hidden sm:table-cell">Email</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-5 py-3">Role</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-5 py-3">Plan</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-5 py-3">Badges</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-5 py-3 w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-border last:border-0" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">{u.name?.charAt(0)?.toUpperCase() || "U"}</div>
                        <span className="text-sm font-medium text-foreground">{u.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-muted-foreground hidden sm:table-cell">{u.email}</td>
                    <td className="px-5 py-3">
                      <select value={u.role} onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        className="text-xs border border-border rounded-lg px-2 py-1 bg-background text-foreground focus:ring-1 focus:ring-primary outline-none"
                        data-testid={`role-select-${u.id}`}>
                        <option value="user">user</option>
                        <option value="moderator">moderator</option>
                        <option value="admin">admin</option>
                      </select>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${PLAN_COLORS[u.subscription_plan || "free"]}`}>
                          {u.subscription_plan === "premium" ? "👑 Premium" : "Free"}
                        </span>
                        {u.subscription_plan === "premium" ? (
                          <button onClick={() => handleRevokePremium(u.id)} className="text-[10px] text-red-500 hover:underline">Revoke</button>
                        ) : (
                          <button onClick={() => handleGrantPremium(u.id)} className="text-[10px] text-primary hover:underline">Grant</button>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        {(Array.isArray(u.badges) ? u.badges : (u.badges ? [u.badges] : [])).map((b) => (
                          <span key={b} className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${BADGE_COLORS[b] || BADGE_COLORS.new_traveler}`}>{typeof b === 'string' ? b.replace("_", " ") : String(b)}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setBadgeUser(u)} className="text-muted-foreground hover:text-primary p-1" title="Edit badges" data-testid={`edit-badges-${u.id}`}>
                          <Award className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        {u.role !== "admin" && (
                          <button onClick={() => handleDeleteUser(u.id, u.name)} className="text-red-400 hover:text-red-600 hover:bg-red-500/10 p-1 rounded transition-colors" title="Delete user" data-testid={`delete-user-${u.id}`}>
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Channels Management */}
      {tab === "channels" && (
        <div className="space-y-3" data-testid="admin-channels-list">
          {channels.map((ch) => (
            <div key={ch.id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-4" data-testid={`admin-channel-${ch.id}`}>
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Hash className="w-5 h-5 text-primary" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm text-foreground">{ch.name}</h3>
                <p className="text-xs text-muted-foreground">{ch.description} - {ch.post_count || 0} posts</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => setEditChannel(ch)} className="text-muted-foreground hover:text-primary p-2 rounded-lg transition-colors" data-testid={`edit-channel-${ch.id}`}>
                  <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button onClick={() => handleDeleteChannel(ch.id)} className="text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-colors" data-testid={`delete-channel-${ch.id}`}>
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Analytics */}
      {tab === "analytics" && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="admin-analytics-view">
          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="font-semibold text-foreground mb-4">Platform Growth</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-muted-foreground">Total Users</span>
                <span className="font-bold text-foreground">{stats.users}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-muted-foreground">Total Posts</span>
                <span className="font-bold text-foreground">{stats.posts}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">Total Comments</span>
                <span className="font-bold text-foreground">{stats.comments}</span>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="font-semibold text-foreground mb-4">Engagement Metrics</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-muted-foreground">Community Likes</span>
                <div className="flex items-center gap-1.5 font-bold text-foreground">
                  <Heart className="w-4 h-4 text-red-500 fill-current" /> {stats.likes}
                </div>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">Engagement Rate</span>
                <div className="flex items-center gap-1.5 font-bold text-foreground">
                  <TrendingUp className="w-4 h-4 text-primary" /> {stats.engagement_rate}%
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {badgeUser && <BadgeModal user={badgeUser} onClose={() => setBadgeUser(null)} onSave={handleUpdateBadges} />}
      {editChannel && <ChannelEditModal channel={editChannel} onClose={() => setEditChannel(null)} onSave={handleUpdateChannel} />}
    </div>
  );
}

function BadgeModal({ user, onClose, onSave }) {
  const [selected, setSelected] = useState(Array.isArray(user.badges) ? user.badges : (user.badges ? [user.badges] : []));
  const toggle = (badge) => setSelected((prev) => prev.includes(badge) ? prev.filter((b) => b !== badge) : [...prev, badge]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="badge-modal">
      <div className="bg-card rounded-2xl p-6 w-full max-w-sm border border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-foreground">Badges for {user.name}</h3>
          <button onClick={onClose} className="text-muted-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-2 mb-6">
          {VALID_BADGES.map((b) => (
            <label key={b} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${selected.includes(b) ? "border-primary bg-primary/5" : "border-border"}`}>
              <input type="checkbox" checked={selected.includes(b)} onChange={() => toggle(b)} className="accent-primary" data-testid={`badge-checkbox-${b}`} />
              <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide border ${BADGE_COLORS[b] || BADGE_COLORS.new_traveler}`}>{b.replace("_", " ")}</span>
            </label>
          ))}
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 border border-border text-foreground hover:bg-muted rounded-full py-2 text-sm font-medium transition-colors">Cancel</button>
          <button onClick={() => onSave(user.id, selected)} className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full py-2 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors" data-testid="save-badges-btn">
            <Save className="w-4 h-4" strokeWidth={1.5} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

function ChannelEditModal({ channel, onClose, onSave }) {
  const [name, setName] = useState(channel.name);
  const [desc, setDesc] = useState(channel.description);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="channel-edit-modal">
      <div className="bg-card rounded-2xl p-6 w-full max-w-sm border border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-foreground">Edit Channel</h3>
          <button onClick={onClose} className="text-muted-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 mb-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="w-full border border-border rounded-lg px-4 py-2 bg-background text-foreground focus:ring-2 focus:ring-primary/50 outline-none text-sm" data-testid="edit-channel-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2}
              className="w-full border border-border rounded-lg px-4 py-2 bg-background text-foreground focus:ring-2 focus:ring-primary/50 outline-none text-sm resize-none" data-testid="edit-channel-desc" />
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 border border-border text-foreground hover:bg-muted rounded-full py-2 text-sm font-medium transition-colors">Cancel</button>
          <button onClick={() => onSave(channel.id, { name, description: desc })} className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full py-2 text-sm font-medium transition-colors" data-testid="save-channel-btn">Save</button>
        </div>
      </div>
    </div>
  );
}
